"use client";

import { useEffect, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import { resolveLink, resolveLinkUntracked } from "@/sanity/globals/CTA";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import type { ITradingCompetitionLink } from "@/sanity/pages/PageTradingCompetitionTemplate";
import { readAffCode, withCheckoutAffCode } from "@/lib/tradingCompetitionAffiliate";

/**
 * The link as the server renders it: language prefix only, no tracking. Hash anchors pass through.
 * `resolveLink` reads the visitor's URL and localStorage, so calling it during render gives the
 * server and the client different hrefs, and React keeps the server attribute on hydration. That is
 * why the CTAs ignored `?aff=` before; `useCtaHref` resolves the tracked link after mount instead.
 */
export function linkHref(link?: ITradingCompetitionLink, language?: string) {
  const url = stegaClean(link?.url);
  if (!url) return undefined;
  return url.startsWith("#") ? url : resolveLinkUntracked(url, stegaClean(language) || DEFAULT_LANGUAGE);
}

/**
 * A CTA href that carries the visitor's tracking params and, for checkout links, the competition
 * affiliate code (see lib/tradingCompetitionAffiliate.ts). Starts as the plain server href so the
 * hydrated HTML matches, then updates in the browser.
 */
export function useCtaHref(link?: ITradingCompetitionLink, language?: string) {
  const url = stegaClean(link?.url) || undefined;
  const lang = stegaClean(language) || DEFAULT_LANGUAGE;
  const [href, setHref] = useState(() => linkHref(link, language));

  useEffect(() => {
    if (!url || url.startsWith("#")) {
      setHref(url);
      return;
    }
    const resolved = resolveLink(url, lang);
    const { code, urlHasAff } = readAffCode();
    // Without a code and without `?aff=` in the URL the link stays exactly as resolveLink made it,
    // including an `aff` an editor may have typed into the CTA itself.
    setHref(code || urlHasAff ? withCheckoutAffCode(resolved, code) : resolved);
  }, [url, lang]);

  return href;
}
